//================================================================
//  Component: Live Field
//================================================================

//  Purpose: onBlur automatically save content to Firebase, handles errors and animations

//  Properties:
//    - type = {'text', 'multiselect', 'choice', 'multiline', 'timestamp' (READ ONLY), 'date-string', 'richtext-editor', 'user-search' (Saves an object), 'lookup' (provide keys), 'number', & 'currency' (SAVED as string for formatting)
//    - collectionid = {String, Firestore collection id}
//    - documentid = {String, Firestore document id}
//    - fieldpath = {String, Firestore field path}
//    - label = {HTML, Label Tag}
//    - defaultValue = {string, onload value for the input}
//    - options = <OPTIONAL> {Array, options for a selectbox dropdown}
//    - disabled = <OPTIONAL> {boolean, enable or disable the input}
//    - styleInput = <OPTIONAL> {object, CSS style object}
//    - placeholder = <OPTIONAL> {string, contains the placeholder text for the input field}
//    - dataFieldCollectionid = <REQUIRED 'lookup'> {string, collectionid to query}
//    - dataFieldPrimary = <REQUIRED 'lookup'> {string, field key which is saved to the Firestore document and SHOW in the drop down}
//    - dataFieldSecondary = <Optional 'lookup'> {string, field key which is saved to the Firestore document and SHOW in the drop down}
//    - dataFieldTertiary = <Optional 'lookup'> {string, field key which is saved to the Firestore document and IS NOT shown in the dropdown. Good for a documentid, etc}
//    - createNewHTML = <Optional 'lookup'> {HTML, generic html attached to the bottom of the search results}
//    - currency = <OPTIONAL 'currency'> {string, currency type for symbols (i.e. '$', '£')} 
//    - validation = <OPTIONAL> {object, contains validation rules based on field type}
//        - type = {string, specifies the validation type, e.g., 'link', 'date'}
//        - minDate = {string, minimum date allowed (for date inputs)}
//        - maxDate = {string, maximum date allowed (for date inputs)}
//    - validationCallBackFunc = <OPTIONAL> {function, accepts a value, and returns back an object,
//        e.g. 
//        {
//           'value': '<Insert modified field value>',
//           'error': '<Insert Error Message>'
//        }
//
//      }

//  Examples:

//    <LiveField
//      type={'text'}
//      collectionid={'agreements'}
//      documentid={agreementid}
//      fieldpath={'agreementstatus'}
//      label={<label className='font-medium'>1. Process Name <span className='text-[#C4314B]'>*</span> </label>}
//      defaultValue={'Active'}
//      options={arrayOfOptions}
//      disabled={false}
//      styleInput={{padding: '5px'}}
//      placeholder={'Select Status'}
//      validation={{
//         type: 'date',
//         minDate : Date()
//         maxDate : Date()
//      }}
//      validationCallBackFunc={(value) => {
// 
//        if (value.length > 100) {
//        
//          return {
//            'value': value.slice(0, 99),
//            'error': 'Yo, that's too many characters, I've trimmed the string to fit in this field!'
//          }
//
//        }
//      
//      }}
//    ></LiveField>    


//    <LiveField
//      type={'lookup'}
//      collectionid={'agreements'}
//      documentid={agreementDoc?.agreementid}
//      fieldpath={'vendor'}
//      label={<label className='font-medium'> Vendor Name </label>}
//      defaultValue={agreementDoc?.vendor?.vendorname}
//      placeholder={'Select a vendor'}
//      dataFieldCollectionid={'vendors'}
//      dataFieldPrimary={'vendorname'}
//      dataFieldSecondary={'tradingas'}
//      dataFieldTertiary={'vendorid'}
//      createNewHTML={<span className='font-normal text-[#A0A0A0]'>Can't find what you're looking for? Click <a href='http://console.digitalservices.lendlease.com/inventory?view=vendors' target='_blank' rel='noopener noreferrer'>here</a>.</span>}
//    ></LiveField> 

//================================================================


//Libraries
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

//Contexts
import { GetUser } from '../../Library/GlobalContexts';

//Components
import UserSearchLiveFields from './Components/UserSearchLiveFields';
import LookUpLiveFields from './Components/LookUpLiveFields';

//Functions
import WriteDocument from '../../Library/WriteDocument';
import ConvertDate from '../../Library/ConvertDate';

//Images
import successIcon from '../Images/Icon_Save_Teal.svg';
import pendingIcon from '../Images/Icon_LoadingFile_Grey.gif';
import errorIcon from '../Images/Icon_Error_Red.svg';

//CSS
import './LiveField.css';


export default function LiveField({
  type,
  collectionid,
  documentid,
  fieldpath,
  label,
  defaultValue,
  options,
  disabled,
  styleInput,
  placeholder,

  // 'currency' type props
  currency,

  // 'lookup' type props
  dataFieldCollectionid,
  dataFieldPrimary,
  dataFieldSecondary,
  dataFieldTertiary,
  dataFieldQuaternary,
  createNewHTML,

  // validation
  validation,
  validationCallBackFunc,
}) {

  //------------------------------------------------------
  //  useContexts
  //------------------------------------------------------

  const getUser = useContext(GetUser);

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Handles the field state --> onload, pending, success, error
  const [fieldStatus, setFieldStatus] = useState('onload');

  // Holds the current field value
  const [fieldValue, setFieldValue] = useState();

  // Holds the current error message
  const [fieldError, setFieldError] = useState('');

  // Check if the app is in development environment
  const isDevelopment = process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285';

  // Check if the app is in uat environment
  const isUAT = process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396';

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  function handleBlur(value, skipFieldValue) {

    setFieldError('');

    // Skip writing NO changes to Firestore
    if ((skipFieldValue === undefined || skipFieldValue === false) && defaultValue === fieldValue) return setFieldStatus('onload');

    //------------------------------------------------------
    // Validation Rules (Using Compact Validation Prop)
    //------------------------------------------------------

    // Link Validation
    if (validation?.type === 'link' && value !== '') {

      // eslint-disable-next-line
      const urlPattern = /^(https?:\/\/)([\w.-]+)(:\d+)?(\/[\w./?%&=-]*)?$/;

      if (!urlPattern.test(value)) {

        setFieldStatus('error');
        return setFieldError('Invalid link format');

      }

    }

    //------------------------------------------------------
    //  Currency Field Validation
    //------------------------------------------------------

    if (type === 'currency') {

      value = value.replaceAll(',', '');

      // Format into something pretty
      if (currency === 'USD') value = new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(value);

      else if (currency === 'GBP') value = new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency }).format(value);

      else if (currency === 'SGD') value = new Intl.NumberFormat('en-SG', { style: 'currency', currency: currency }).format(value);

      else if (currency === 'EUR') value = new Intl.NumberFormat('en-IE', { style: 'currency', currency: currency }).format(value);

      else if (currency === 'AUD') value = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);

      // Default value is nothing
      else value = 0;

      setFieldValue(value);

    }

    //------------------------------------------------------
    //  Date Field Validation
    //------------------------------------------------------

    if (type === 'date-string') {

      // If value is less than minimum date > Show error
      if (value < validation?.minDate) {

        setFieldStatus('error');
        return setFieldError(`Date must be after ${validation?.minDate}`);

      }


      // If value is greater than maximum date > Show error
      if (value > validation?.maxDate) {

        setFieldStatus('error');
        return setFieldError(`Date must be before ${validation?.maxDate}`);

      }

    }

    //------------------------------------------------------
    //  Custom validation function
    //------------------------------------------------------

    if (validationCallBackFunc !== undefined) {

      // Trigger CB function to handle validation.
      const response = validationCallBackFunc(value);

      // Save the modified value from the CB function
      if (response?.value !== undefined && response.value.length > 0) {

        value = response.value;
        setFieldValue(response.value);

      }

      // I'm expecting a string with error message if something fails.
      if (response?.error !== undefined && response.error.length > 0) {

        setFieldStatus('error');
        return setFieldError(response.error);

      }

    }

    //------------------------------------------------------
    //  Passed validation
    //------------------------------------------------------

    setFieldStatus('pending');

    // Prepare firestore document
    let document = {
      'lastmodifieddate': new Date(),
      'lastmodifiedby': {
        'email': getUser?.emailaddress,
        'givenname': getUser?.givenname,
        'surname': getUser?.surname
      }
    };

    //------------------------------------------------------
    //  Handle nested fieldpaths & add value to Firestore document
    //------------------------------------------------------

    if (fieldpath.split('.')?.length > 0) {

      const keys = fieldpath.split('.');
      const results = {};

      if (keys.length === 1) {

        results[keys[0]] = value;

      }
      if (keys.length === 2) {

        results[keys[0]] = {
          [keys[1]]: value
        };

      }
      if (keys.length === 3) {

        results[keys[0]] = {
          [keys[1]]: {
            [keys[2]]: value
          }
        };

      }
      if (keys.length === 4) {

        results[keys[0]] = {
          [keys[1]]: {
            [keys[2]]: {
              [keys[3]]: value
            }
          }
        };

      }

      if (keys.length === 5) return setFieldError('Unable to support this many nested fields!!!!');

      // Merge the objects
      Object.assign(document, results);

    }

    //------------------------------------------------------
    //  No nested fields --> Add value to firestore document
    //------------------------------------------------------

    if (fieldpath.split('.')?.length === 0) {

      document[fieldpath] = value;

    }

    //------------------------------------------------------
    //  Proceed with updating document in Firestore
    //------------------------------------------------------

    WriteDocument(collectionid, documentid, document, true).then(() => {

      setFieldStatus('success');
      setFieldError('');

      // Reset the field status after a 5 second delay
      return setTimeout(() => {

        setFieldStatus('onload');

      }, 5000);

    }).catch((error) => {

      console.log('error', error);
      setFieldStatus('error');

    });

  }

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  // Handles field updates from document listeners
  useEffect(() => {

    if (defaultValue === undefined) return;

    setFieldValue(defaultValue);

  }, [defaultValue]);

  //------------------------------------------------------
  //  Text Field Input
  //------------------------------------------------------

  if (type === 'text') {
    return (
      <div className='FormComponent-Row'>

        {label}

        {
          // onload
          fieldStatus === 'onload' ?
            (
              <input
                style={styleInput}
                className='Input-Field-Text'
                type='text'
                value={fieldValue}
                onChange={(e) => setFieldValue(e.target.value)}
                onBlur={() => handleBlur(fieldValue)}
                disabled={disabled}
                placeholder={placeholder}
              ></input>
            )
            :

            // pending
            fieldStatus === 'pending' ?
              (

                <div className='flex flex-row gap-2'>
                  <input
                    style={styleInput}
                    className='Input-Field-Text'
                    type='text'
                    value={fieldValue}
                    disabled
                  ></input>
                  <img className='w-[30px] h-[30px] self-center' src={pendingIcon} alt='pending-icon'></img>
                </div>

              )
              :

              // success
              fieldStatus === 'success' ?
                (

                  <div className='flex flex-row gap-2'>
                    <input
                      style={styleInput}
                      className='Input-Field-Text'
                      type='text'
                      value={fieldValue}
                      onChange={(e) => setFieldValue(e.target.value)}
                      onBlur={() => handleBlur(fieldValue)}
                      disabled={disabled}
                    ></input>
                    <img className='LiveField-Success' src={successIcon} alt='success-icon'></img>
                  </div>

                )
                :

                // Catch All --> error
                (

                  <div className='flex flex-col gap-0'>
                    <div className='flex flex-row gap-2'>
                      <input
                        style={styleInput}
                        className='Input-Field-Text-Error'
                        type='text'
                        value={fieldValue}
                        onChange={(e) => setFieldValue(e.target.value)}
                        onBlur={() => handleBlur(fieldValue)}
                      ></input>
                      <img className='w-[30px]' src={errorIcon} alt='error-icon'></img>
                    </div>

                    {/* Custom or generic error message */}
                    <label className='text-[#DE0000]'>
                      {
                        fieldError ?
                          <>
                            {fieldError}
                          </>
                          :
                          <>
                            Unable to save this field. Please try again, or contact us <Link className='underline' target='_blank' to={isDevelopment ? 'https://dev.digitalservices.lendlease.com/help' : isUAT ? 'https://uat.digitalservices.lendlease.com/help' : 'https://digitalservices.lendlease.com/help'}>here</Link> if the issue persists.
                          </>

                      }
                    </label>

                  </div>

                )

        }

      </div>
    )

  }

  //------------------------------------------------------
  //   Multi Select
  //------------------------------------------------------

  else if (type === 'multiselect') {

    if (options === undefined || options.length === 0) return 'You are missing the "options" prop'

    return (
      <div className='FormComponent-Row'>

        {label}

        {
          // onload
          fieldStatus === 'onload' ? (

            <div className='flex flex-col gap-0'>
              <p hidden={disabled} className='text-sm text-slate-600 mb-2 p-0'>To select more than one, press Ctrl and select your options.</p>

              <select
                style={styleInput}
                className='Input-Field-MultiSelect'
                onChange={(e) => setFieldValue(Array.from(e.target.selectedOptions, (option) => option.value))}
                onBlur={() => handleBlur(fieldValue)}
                value={fieldValue}
                multiple
                disabled={disabled}
              >
                <option hidden value=''>-</option>
                {
                  options.map((choice, index) => (
                    <option key={index} value={choice}>{choice}</option>
                  ))
                }
              </select>
            </div>

          ) :

            // pending
            fieldStatus === 'pending' ? (

              <div className='flex flex-col gap-0'>
                <p hidden={disabled} className='text-sm text-slate-600 mb-2 p-0'>To select more than one, press Ctrl and select your options.</p>

                <div className='flex flex-row gap-2'>
                  <select
                    style={styleInput}
                    className='Input-Field-MultiSelect'
                    multiple
                    value={fieldValue}
                    disable
                  >
                    <option hidden value=''>-</option>
                    {
                      options.map((choice, index) => (
                        <option key={index} value={choice}>{choice}</option>
                      ))
                    }

                  </select>
                  <img className='w-[30px] h-[30px] self-center' src={pendingIcon} alt='pending-icon'></img>
                </div>
              </div>

            ) :

              // success
              fieldStatus === 'success' ? (

                <div className='flex flex-col gap-0'>
                  <p hidden={disabled} className='text-sm text-slate-600 mb-2 p-0'>To select more than one, press Ctrl and select your options.</p>

                  <div className='flex flex-row gap-2'>
                    <select
                      style={styleInput}
                      className='Input-Field-MultiSelect'
                      onChange={(e) => setFieldValue(Array.from(e.target.selectedOptions, (option) => option.value))}
                      onBlur={() => handleBlur(fieldValue)}
                      value={fieldValue}
                      multiple
                      disabled={disabled}
                    >
                      <option hidden value=''>-</option>
                      {
                        options.map((choice, index) => (
                          <option key={index} value={choice}>{choice}</option>
                        ))
                      }

                    </select>
                    <img className='LiveField-Success' src={successIcon} alt='success-icon'></img>
                  </div>
                </div>

              ) :

                // Catch All --> error
                (

                  <div className='flex flex-col gap-0'>
                    <p hidden={disabled} className='text-sm text-slate-600 mb-2 p-0'>To select more than one, press Ctrl and select your options.</p>

                    <div className='flex flex-row gap-2'>
                      <select
                        style={styleInput}
                        className='Input-Field-MultiSelect-Error'
                        onChange={(e) => setFieldValue(Array.from(e.target.selectedOptions, (option) => option.value))}
                        onBlur={() => handleBlur(fieldValue)}
                        value={fieldValue}
                        multiple
                      >
                        <option hidden value=''>-</option>
                        {
                          options.map((choice, index) => (
                            <option key={index} value={choice}>{choice}</option>
                          ))
                        }
                      </select>
                      <img className='w-[30px]' src={errorIcon} alt='error-icon'></img>
                    </div>


                    {/* Custom or generic error message */}
                    <label className='text-[#DE0000]'>
                      {
                        fieldError ?
                          <>
                            {fieldError}
                          </>
                          :
                          <>
                            Unable to save this field. Please try again, or contact us <Link className='underline' target='_blank' to={isDevelopment ? 'https://dev.digitalservices.lendlease.com/help' : isUAT ? 'https://uat.digitalservices.lendlease.com/help' : 'https://digitalservices.lendlease.com/help'}>here</Link> if the issue persists.
                          </>

                      }
                    </label>

                  </div>

                )
        }

      </div>
    )

  }

  //------------------------------------------------------
  //   Choice (Single)
  //------------------------------------------------------

  else if (type === 'choice') {

    if (options === undefined || options.length === 0) return 'You are missing the "options" prop'

    return (
      <div className='FormComponent-Row'>

        {label}

        {
          // onload
          fieldStatus === 'onload' ? (

            <div className='flex flex-col gap-0'>
              <select
                style={styleInput}
                className='Input-Field-Select'
                onChange={(e) => setFieldValue(e.target.value)}
                onBlur={() => handleBlur(fieldValue)}
                value={fieldValue}
                disabled={disabled}
              >
                <option hidden value=''>-</option>
                {
                  options.map((choice, index) => (
                    <option key={index} value={choice}>{choice}</option>
                  ))
                }

              </select>
            </div>

          ) :

            // pending
            fieldStatus === 'pending' ? (

              <div className='flex flex-row gap-2'>

                <select
                  style={styleInput}
                  className='Input-Field-Select'
                  value={fieldValue}
                  disabled
                >
                  <option hidden value=''>-</option>
                  {
                    options.map((choice, index) => (
                      <option key={index} value={choice}>{choice}</option>
                    ))
                  }

                </select>

                <img className='w-[30px] h-[30px] self-center' src={pendingIcon} alt='pending-icon'></img>
              </div>

            ) :

              // success
              fieldStatus === 'success' ? (

                <div className='flex flex-row gap-2'>

                  <select
                    style={styleInput}
                    className='Input-Field-Select'
                    onChange={(e) => setFieldValue(e.target.value)}
                    onBlur={() => handleBlur(fieldValue)}
                    value={fieldValue}
                    disabled={disabled}
                  >
                    <option hidden value=''>-</option>
                    {
                      options.map((choice, index) => (
                        <option key={index} value={choice}>{choice}</option>
                      ))
                    }

                  </select>

                  <img className='LiveField-Success' src={successIcon} alt='success-icon'></img>

                </div>

              ) :

                // Catch All --> error
                (

                  <div className='flex flex-col gap-0'>

                    <div className='flex flex-row gap-2'>

                      <select
                        style={styleInput}
                        className='Input-Field-Select-Error'
                        onChange={(e) => setFieldValue(e.target.value)}
                        onBlur={() => handleBlur(fieldValue)}
                        value={fieldValue}
                      >
                        <option hidden value=''>-</option>
                        {
                          options.map((choice, index) => (
                            <option key={index} value={choice}>{choice}</option>
                          ))
                        }

                      </select>

                      <img className='w-[30px]' src={errorIcon} alt='error-icon'></img>

                    </div>

                    {/* Custom or generic error */}
                    <label className='text-[#DE0000]'>
                      {
                        fieldError ?
                          <>
                            {fieldError}
                          </>
                          :
                          <>
                            Unable to save this field. Please try again, or contact us <Link className='underline' target='_blank' to={isDevelopment ? 'https://dev.digitalservices.lendlease.com/help' : isUAT ? 'https://uat.digitalservices.lendlease.com/help' : 'https://digitalservices.lendlease.com/help'}>here</Link> if the issue persists.
                          </>

                      }
                    </label>

                  </div>

                )

        }

      </div>
    )

  }

  //------------------------------------------------------
  //   Multiline (Text)
  //------------------------------------------------------

  else if (type === 'multiline') {

    return (
      <div className='FormComponent-Row'>

        {label}

        {
          // onload
          fieldStatus === 'onload' ? (

            <div className='flex flex-col gap-0 max-w-[95%]'>
              <textarea
                style={styleInput}
                className='Input-Field-TextArea'
                value={fieldValue}
                onChange={(e) => setFieldValue(e.target.value)}
                onBlur={() => handleBlur(fieldValue)}
                disabled={disabled}
                placeholder={placeholder}
              ></textarea>
            </div>

          ) :

            // pending
            fieldStatus === 'pending' ? (

              <div className='flex flex-row gap-2 max-w-[95%]'>

                <textarea
                  style={styleInput}
                  className='Input-Field-TextArea'
                  value={fieldValue}
                  disabled={true}
                ></textarea>

                <img className='w-[30px] h-[30px] self-center' src={pendingIcon} alt='pending-icon'></img>
              </div>

            ) :

              // success
              fieldStatus === 'success' ? (

                <div className='flex flex-row gap-2 max-w-[95%]'>

                  <textarea
                    style={styleInput}
                    className='Input-Field-TextArea'
                    value={fieldValue}
                    onChange={(e) => setFieldValue(e.target.value)}
                    onBlur={() => handleBlur(fieldValue)}
                    disabled={disabled}
                  ></textarea>

                  <img className='LiveField-Success' src={successIcon} alt='success-icon'></img>

                </div>

              ) :

                // Catch All --> error
                (

                  <div className='flex flex-col gap-0 max-w-[95%]'>

                    <div className='flex flex-row gap-2'>

                      <textarea
                        style={styleInput}
                        className='Input-Field-TextArea-Error'
                        value={fieldValue}
                        onChange={(e) => setFieldValue(e.target.value)}
                        onBlur={() => handleBlur(fieldValue)}
                        disabled={disabled}
                      ></textarea>

                      <img className='w-[30px]' src={errorIcon} alt='error-icon'></img>

                    </div>

                    {/* Custom or generic error message */}
                    <label className='text-[#DE0000]'>
                      {
                        fieldError ?
                          <>
                            {fieldError}
                          </>
                          :
                          <>
                            Unable to save this field. Please try again, or contact us <Link className='underline' target='_blank' to={isDevelopment ? 'https://dev.digitalservices.lendlease.com/help' : isUAT ? 'https://uat.digitalservices.lendlease.com/help' : 'https://digitalservices.lendlease.com/help'}>here</Link> if the issue persists.
                          </>

                      }
                    </label>

                  </div>

                )

        }

      </div>
    )

  }

  //------------------------------------------------------
  //   Date String
  //------------------------------------------------------

  else if (type === 'date-string') {

    return (
      <div className='FormComponent-Row'>

        {label}

        {
          // onload
          fieldStatus === 'onload' ? (

            <input
              style={styleInput}
              className='Input-Field-Text'
              type='date'
              placeholder='Enter date'
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
              onBlur={() => handleBlur(fieldValue)}
              disabled={disabled}
              min={validation?.minDate}
              max={validation?.maxDate}
            ></input>

          ) :

            // pending
            fieldStatus === 'pending' ? (

              <div className='flex flex-row gap-2'>
                <input
                  style={styleInput}
                  className='Input-Field-Text'
                  type='date'
                  placeholder='Enter date'
                  value={fieldValue}
                  disabled={true}
                ></input>
                <img className='w-[30px] h-[30px] self-center' src={pendingIcon} alt='pending-icon'></img>
              </div>

            ) :

              // success
              fieldStatus === 'success' ? (

                <div className='flex flex-row gap-2'>
                  <input
                    style={styleInput}
                    className='Input-Field-Text'
                    type='date'
                    placeholder='Enter date'
                    value={fieldValue}
                    onChange={(e) => setFieldValue(e.target.value)}
                    onBlur={() => handleBlur(fieldValue)}
                    disabled={disabled}
                  ></input>
                  <img className='LiveField-Success' src={successIcon} alt='success-icon'></img>
                </div>

              ) :

                // Catch All --> error
                (

                  <div className='flex flex-col gap-0'>
                    <div className='flex flex-row gap-2'>
                      <input
                        style={styleInput}
                        className='Input-Field-Text-Error'
                        type='date'
                        placeholder='Enter date'
                        value={fieldValue}
                        onChange={(e) => setFieldValue(e.target.value)}
                        onBlur={() => handleBlur(fieldValue)}
                        disabled={disabled}
                      ></input>
                      <img className='w-[30px]' src={errorIcon} alt='error-icon'></img>
                    </div>

                    {/* Custom or generic error message */}
                    <label className='text-[#DE0000]'>
                      {
                        fieldError ?
                          <>
                            {fieldError}
                          </>
                          :
                          <>
                            Unable to save this field. Please try again, or contact us <Link className='underline' target='_blank' to={isDevelopment ? 'https://dev.digitalservices.lendlease.com/help' : isUAT ? 'https://uat.digitalservices.lendlease.com/help' : 'https://digitalservices.lendlease.com/help'}>here</Link> if the issue persists.
                          </>

                      }
                    </label>

                  </div>

                )

        }

      </div>
    )

  }

  //------------------------------------------------------
  //   Timestamp (Read Only)
  //------------------------------------------------------

  else if (type === 'timestamp') {

    return (
      <div className='FormComponent-Row'>

        {label}

        <input
          style={styleInput}
          className='Input-Field-Text'
          type='text'
          value={ConvertDate(fieldValue)}
          disabled={true}
        ></input>

      </div>
    )

  }

  //------------------------------------------------------
  //  Number
  //------------------------------------------------------

  else if (type === 'number') {
    return (
      <div className='FormComponent-Row'>

        {label}

        {
          // onload
          fieldStatus === 'onload' ?
            (

              <div>


                <input
                  style={styleInput}
                  className='Input-Field-Text'
                  type='number'
                  value={fieldValue}
                  onChange={(e) => setFieldValue(e.target.value)}
                  onBlur={() => handleBlur(fieldValue)}
                  disabled={disabled}
                  placeholder={placeholder}
                ></input>

                {
                  fieldError !== '' ?
                    <label className='text-[#DE0000]'>Unable to save this field. Please try again, or contact us <Link className='underline' target='_blank' to={isDevelopment ? 'https://dev.digitalservices.lendlease.com/help' : isUAT ? 'https://uat.digitalservices.lendlease.com/help' : 'https://digitalservices.lendlease.com/help'}>here</Link> if the issue persists.</label> : null
                }

              </div>

            )
            :

            // pending
            fieldStatus === 'pending' ?
              (

                <div className='flex flex-row gap-2'>
                  <input
                    style={styleInput}
                    className='Input-Field-Text'
                    type='number'
                    value={fieldValue}
                    disabled={true}
                  ></input>
                  <img className='w-[30px] h-[30px] self-center' src={pendingIcon} alt='pending-icon'></img>
                </div>

              )
              :

              // success
              fieldStatus === 'success' ?
                (

                  <div className='flex flex-row gap-2'>
                    <input
                      style={styleInput}
                      className='Input-Field-Text'
                      type='number'
                      value={fieldValue}
                      onChange={(e) => setFieldValue(e.target.value)}
                      onBlur={() => handleBlur(fieldValue)}
                      disabled={disabled}
                    ></input>
                    <img className='LiveField-Success' src={successIcon} alt='success-icon'></img>
                  </div>

                )
                :

                // Catch All --> error
                (

                  <div className='flex flex-col gap-0'>
                    <div className='flex flex-row gap-2'>
                      <input
                        style={styleInput}
                        className='Input-Field-Text-Error'
                        type='number'
                        value={fieldValue}
                        onChange={(e) => setFieldValue(e.target.value)}
                        onBlur={() => handleBlur(fieldValue)}
                        disabled={disabled}
                      ></input>
                      <img className='w-[30px]' src={errorIcon} alt='error-icon'></img>
                    </div>

                    {/* Custom or generic error message */}
                    <label className='text-[#DE0000]'>
                      {
                        fieldError ?
                          <>
                            {fieldError}
                          </>
                          :
                          <>
                            Unable to save this field. Please try again, or contact us <Link className='underline' target='_blank' to={isDevelopment ? 'https://dev.digitalservices.lendlease.com/help' : isUAT ? 'https://uat.digitalservices.lendlease.com/help' : 'https://digitalservices.lendlease.com/help'}>here</Link> if the issue persists.
                          </>

                      }
                    </label>

                  </div>

                )

        }

      </div>
    )

  }


  //------------------------------------------------------
  //   Currency
  //------------------------------------------------------

  else if (type === 'currency') {

    return (
      <div className='FormComponent-Row'>

        {label}

        {
          // onload
          fieldStatus === 'onload' ? (

            <input
              style={styleInput}
              className='Input-Field-Text'
              type='text'
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
              onBlur={() => handleBlur(fieldValue)}
              disabled={disabled}
              placeholder={placeholder}
            ></input>

          ) :

            // pending
            fieldStatus === 'pending' ? (

              <div className='flex flex-row gap-2'>
                <input
                  style={styleInput}
                  className='Input-Field-Text'
                  type='text'
                  value={fieldValue}
                  disabled
                  placeholder={placeholder}
                ></input>
                <img className='w-[30px] h-[30px] self-center' src={pendingIcon} alt='pending-icon'></img>
              </div>

            ) :

              // success
              fieldStatus === 'success' ? (

                <div className='flex flex-row gap-2'>
                  <input
                    style={styleInput}
                    className='Input-Field-Text'
                    type='text'
                    value={fieldValue}
                    onChange={(e) => setFieldValue(e.target.value)}
                    onBlur={() => handleBlur(fieldValue)}
                    disabled={disabled}
                    placeholder={placeholder}
                  ></input>
                  <img className='LiveField-Success' src={successIcon} alt='success-icon'></img>
                </div>

              ) :

                // Catch All --> error
                (

                  <div className='flex flex-col gap-0'>
                    <div className='flex flex-row gap-2'>
                      <input
                        style={styleInput}
                        className='Input-Field-Text-Error'
                        type='text'
                        value={fieldValue}
                        onChange={(e) => setFieldValue(e.target.value)}
                        onBlur={() => setFieldValue('')}
                        placeholder={placeholder}
                      ></input>
                      <img className='w-[30px]' src={errorIcon} alt='error-icon'></img>
                    </div>

                    {/* Custom or generic error message */}
                    <label className='text-[#DE0000]'>
                      {
                        fieldError ?
                          <>
                            {fieldError}
                          </>
                          :
                          <>
                            Unable to save this field. Please try again, or contact us <Link className='underline' target='_blank' to={isDevelopment ? 'https://dev.digitalservices.lendlease.com/help' : isUAT ? 'https://uat.digitalservices.lendlease.com/help' : 'https://digitalservices.lendlease.com/help'}>here</Link> if the issue persists.
                          </>

                      }
                    </label>

                  </div>

                )

        }

      </div>
    )

  }

  //------------------------------------------------------
  //   Radio Buttons
  //------------------------------------------------------

  else if (type === 'radio') {

    return (
      <div className='FormComponent-Row'>

        {label}

        {
          // onload
          fieldStatus === 'onload' ? (

            <div className='flex flex-row gap-2 px-[5px] py-[0px]'>
              <label className='Radio-Buttons-Container'>
                Yes
                <input
                  type='radio'
                  value='yes'
                  checked={fieldValue === 'yes'}
                  onChange={(e) => setFieldValue(e.target.value)}
                  onBlur={() => handleBlur(fieldValue)}
                  disabled={disabled}
                ></input>
                <span className='Radio-Checkmark'></span>
              </label>

              <label className='Radio-Buttons-Container'>
                No
                <input
                  type='radio'
                  value='no'
                  checked={fieldValue === 'no'}
                  onChange={(e) => setFieldValue(e.target.value)}
                  onBlur={() => handleBlur(fieldValue)}
                  disabled={disabled}
                ></input>
                <span className='Radio-Checkmark'></span>
              </label>
            </div>

          )
            :

            // pending
            fieldStatus === 'pending' ? (

              <div className='flex flex-row gap-2'>

                <div className='flex flex-row gap-2 px-[5px] py-[0px]'>
                  <label className='Radio-Buttons-Container'>
                    Yes
                    <input
                      type='radio'
                      value='yes'
                      checked={fieldValue === 'yes'}
                      disabled={disabled}
                    ></input>
                    <span className='Radio-Checkmark'></span>
                  </label>

                  <label className='Radio-Buttons-Container'>
                    No
                    <input
                      type='radio'
                      value='no'
                      checked={fieldValue === 'no'}
                      disabled={disabled}
                    ></input>
                    <span className='Radio-Checkmark'></span>
                  </label>
                </div>

                <img className='w-[30px] h-[30px] self-center' src={pendingIcon} alt='pending-icon'></img>

              </div>

            ) :

              // success
              fieldStatus === 'success' ? (

                <div className='flex flex-row gap-2'>

                  <div className='flex flex-row gap-2 px-[5px] py-[0px]'>
                    <label className='Radio-Buttons-Container'>
                      Yes
                      <input
                        type='radio'
                        value='yes'
                        checked={fieldValue === 'yes'}
                        onChange={(e) => setFieldValue(e.target.value)}
                        onBlur={() => handleBlur(fieldValue)}
                        disabled={disabled}
                      ></input>
                      <span className='Radio-Checkmark'></span>
                    </label>

                    <label className='Radio-Buttons-Container'>
                      No
                      <input
                        type='radio'
                        value='no'
                        checked={fieldValue === 'no'}
                        onChange={(e) => setFieldValue(e.target.value)}
                        onBlur={() => handleBlur(fieldValue)}
                        disabled={disabled}
                      ></input>
                      <span className='Radio-Checkmark'></span>
                    </label>
                  </div>

                  <img className='LiveField-Success' src={successIcon} alt='success-icon'></img>

                </div>

              ) :

                // Catch All --> error
                (

                  <div className='flex flex-col gap-0'>

                    <div className='flex flex-row gap-2 px-[5px] py-[0px]'>
                      <label className='Radio-Buttons-Container'>
                        Yes
                        <input
                          type='radio'
                          value='yes'
                          checked={fieldValue === 'yes'}
                          onChange={(e) => setFieldValue(e.target.value)}
                          onBlur={() => handleBlur(fieldValue)}
                          disabled={disabled}
                        ></input>
                        <span className={fieldError ? 'Radio-Checkmark-Error' : 'Radio-Checkmark'}></span>
                      </label>

                      <label className='Radio-Buttons-Container'>
                        No
                        <input
                          type='radio'
                          value='no'
                          checked={fieldValue === 'no'}
                          onChange={(e) => setFieldValue(e.target.value)}
                          onBlur={() => handleBlur(fieldValue)}
                          disabled={disabled}
                        ></input>
                        <span className={fieldError ? 'Radio-Checkmark-Error' : 'Radio-Checkmark'}></span>
                      </label>
                    </div>

                    {/* Custom or generic error message */}
                    <label className='text-[#DE0000]'>
                      {
                        fieldError ?
                          <>
                            {fieldError}
                          </>
                          :
                          <>
                            Unable to save this field. Please try again, or contact us <Link className='underline' target='_blank' to={isDevelopment ? 'https://dev.digitalservices.lendlease.com/help' : isUAT ? 'https://uat.digitalservices.lendlease.com/help' : 'https://digitalservices.lendlease.com/help'}>here</Link> if the issue persists.
                          </>

                      }
                    </label>

                  </div>

                )

        }

      </div>
    )

  }

  //------------------------------------------------------
  //   User Search
  //------------------------------------------------------

  else if (type === 'user-search') {

    return (
      <div className='FormComponent-Row'>

        {label}

        {
          // onload
          fieldStatus === 'onload' ?
            (
              <UserSearchLiveFields
                defaultValue={defaultValue}
                errors={fieldStatus === 'error' ? true : false}
                handleBlur={handleBlur}
                disabled={disabled}
              ></UserSearchLiveFields>
            )
            :

            // pending
            fieldStatus === 'pending' ?
              (
                <div className='flex flex-row gap-2'>

                  <UserSearchLiveFields
                    defaultValue={defaultValue}
                    errors={fieldStatus === 'error' ? true : false}
                    handleBlur={handleBlur}
                    disabled={true}
                  ></UserSearchLiveFields>

                  <img className='w-[30px] h-[30px] self-center' src={pendingIcon} alt='pending-icon'></img>

                </div>
              )
              :

              // success
              fieldStatus === 'success' ?
                (
                  <div className='flex flex-row gap-2'>

                    <UserSearchLiveFields
                      defaultValue={defaultValue}
                      errors={fieldStatus === 'error' ? true : false}
                      handleBlur={handleBlur}
                      disabled={disabled}
                    ></UserSearchLiveFields>

                    <img className='LiveField-Success' src={successIcon} alt='success-icon'></img>

                  </div>
                )
                :

                // Catch All --> error
                (
                  <div className='flex flex-col gap-0'>

                    <div className='flex flex-row gap-2'>

                      <UserSearchLiveFields
                        defaultValue={defaultValue}
                        errors={fieldStatus === 'error' ? true : false}
                        handleBlur={handleBlur}
                        error={true}
                        disabled={disabled}
                      ></UserSearchLiveFields>

                      <img className='w-[30px]' src={errorIcon} alt='error-icon'></img>

                    </div>

                    {/* Custom or generic error message */}
                    <label className='text-[#DE0000]'>
                      {
                        fieldError ?
                          <>
                            {fieldError}
                          </>
                          :
                          <>
                            Unable to save this field. Please try again, or contact us <Link className='underline' target='_blank' to={isDevelopment ? 'https://dev.digitalservices.lendlease.com/help' : isUAT ? 'https://uat.digitalservices.lendlease.com/help' : 'https://digitalservices.lendlease.com/help'}>here</Link> if the issue persists.
                          </>

                      }
                    </label>

                  </div>
                )

        }

      </div>
    )

  }


  //------------------------------------------------------
  //   Lookup
  //------------------------------------------------------

  else if (type === 'lookup') {
    return (
      <div className='FormComponent-Row'>

        {label}

        <LookUpLiveFields
          collectionid={dataFieldCollectionid}
          defaultValue={defaultValue}
          placeholder={placeholder}
          errors={fieldStatus === 'error' ? true : false}
          disabled={disabled}
          handleBlur={handleBlur}
          fieldStatus={fieldStatus}
          fieldError={fieldError}
          dataFieldPrimary={dataFieldPrimary}
          dataFieldSecondary={dataFieldSecondary}
          dataFieldTertiary={dataFieldTertiary}
          dataFieldQuaternary={dataFieldQuaternary}
          createNewHTML={createNewHTML}
        ></LookUpLiveFields>

      </div>
    )

  }

  //------------------------------------------------------
  //   richtext-editor
  //------------------------------------------------------

  else if (type === 'richtext-editor') {

    return (
      <div>
        Benno-30-05-2025 ~ disabled in the current release
        <br></br>
        Cross-site Scripting in quill
        <br></br>
        https://github.com/advisories/GHSA-4943-9vgg-gr5r
      </div>
    )

    // return (
    //   <div className='FormComponent-Row'>

    //     {label}

    //     <RichTextEditor
    //       disabled={disabled}
    //       fieldStatus={fieldStatus}
    //       fieldError={fieldError}
    //       fieldValue={fieldValue}
    //       setFieldValue={setFieldValue}
    //       handleBlur={handleBlur}
    //       placeholder={placeholder}
    //     ></RichTextEditor>

    //   </div>

    // )

  }

}
